import { useQuery } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'
import { toast } from 'sonner'

import { useState } from 'react'

import { Badge, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, PosButton } from '@/components/ui'
import { PageHeader } from '@/components/pos/PageHeader'
import { OfficeTable } from '@/components/pos/office/OfficeTable'
import { useSession, useSyncState } from '@/contexts/SessionContext'
import { rows } from '@/db/database'
import { useOnline } from '@/hooks/features/office'
import { OfficeModel } from '@/http/models'
import { syncEngine } from '@/sync/SyncEngine'

/**
 * The branch's staff (B21): who works here (`Branch.staff_ids`) plus admins, who work everywhere. The list is the
 * device's copy; a PIN reset needs the server, which makes the PIN and shows it once (POS.md §15.10 item 5).
 */
export const StaffPage = () => {
  const { device, can } = useSession()
  const sync = useSyncState()
  const online = useOnline()
  const [pin, setPin] = useState<{ name: string; pin: string } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const { data: staff = [] } = useQuery({
    queryKey: ['branch-staff', device?.branch_id, sync.lastSyncedAt],
    queryFn: async () => {
      const [branch, users, roles] = await Promise.all([
        rows('branches').where('_id').equals(device!.branch_id).first(),
        rows('users').toArray(),
        rows('roles').toArray(),
      ])
      const ids = ((branch?.staff_ids as string[] | undefined) ?? []).map(String)
      const adminRoles = new Set(roles.filter((r) => r.key === 'admin').map((r) => String(r._id)))
      return users
        .filter((u) => !u.deleted_at)
        .map((u) => {
          const roleIds = ((u.role_ids as string[] | undefined) ?? []).map(String)
          return {
            row: u,
            admin: roleIds.some((id) => adminRoles.has(id)),
            roleNames: roles.filter((r) => roleIds.includes(String(r._id))).map((r) => String(r.name)),
          }
        })
        .filter((s) => s.admin || ids.includes(String(s.row._id)))
        .sort((a, b) => String(a.row.name).localeCompare(String(b.row.name)))
    },
    enabled: !!device,
  })

  const resetPin = async (id: string, name: string) => {
    setBusy(id)
    const res = await OfficeModel.resetPin(id)
    setBusy(null)
    if (!res.success || !res.data?.offline_pin) return toast.error(res.error?.message ?? 'Could not reset the PIN.')
    setPin({ name, pin: res.data.offline_pin })
    // The tills of the branch get the new PIN with their next pull.
    syncEngine.requestSync()
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Staff" subtitle="Who works in this branch. A PIN reset needs a connection." />
      <div className="min-h-0 flex-1 overflow-auto p-4 md:p-6">
        <OfficeTable
          rows={staff}
          rowKey={(s) => s.row.id}
          empty="No staff assigned to this branch."
          columns={[
            { label: 'Name', cell: (s) => <span className="font-medium text-ink">{String(s.row.name)}</span> },
            { label: 'Email', className: 'hidden md:table-cell text-text-2', cell: (s) => String(s.row.email ?? '') },
            {
              label: 'Roles',
              cell: (s) => (
                <span className="flex flex-wrap gap-1">
                  {s.admin && <Badge tone="dark">Admin</Badge>}
                  {s.roleNames.map((n) => (
                    <Badge key={n}>{n}</Badge>
                  ))}
                </span>
              ),
            },
            { label: 'Card', className: 'hidden lg:table-cell font-mono text-xs', cell: (s) => String(s.row.barcode ?? '') },
            {
              label: '',
              className: 'text-right',
              cell: (s) =>
                can('admins:update') && (
                  <PosButton
                    variant="outline"
                    disabled={!online || busy === s.row.id}
                    title={online ? undefined : 'Needs a connection'}
                    onClick={() => resetPin(String(s.row._id), String(s.row.name))}
                  >
                    <KeyRound /> Reset PIN
                  </PosButton>
                ),
            },
          ]}
        />
      </div>
      <Dialog open={!!pin} onOpenChange={(o) => !o && setPin(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>New PIN for {pin?.name}</DialogTitle>
            <DialogDescription>Shown once. This branch's devices accept it after their next sync.</DialogDescription>
          </DialogHeader>
          <p className="py-4 text-center font-mono text-heading-lg tracking-widest text-ink">{pin?.pin}</p>
          <DialogFooter>
            <PosButton onClick={() => setPin(null)}>Done</PosButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
